import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import useDataContext from "../../providers/DataProvider/useDataContext";
import { subscribeToScroll } from "../../assets/scripts/ScrollManager";
import { ChatIcon, CloseIcon } from "../Icons/Icons";
import "./ChatButton.css"

// Id configurado como botón iniciador externo en el dashboard de Cloudbeds Guest Experience .
export const CHAT_LAUNCHER_BUTTON_ID = "chat-launcher-button";

// Id del contenedor que el script de Whistle/Cloudbeds inyecta en el <body> para alojar el iframe del chat.
const CHAT_WIDGET_CONTAINER_SELECTOR = "#w-live-chat";

// Tamaño mínimo que funciona como trigger para saber que el chatbot está abierto.
const OPEN_SIZE_THRESHOLD_PX = 150;

// Normalizar la comparación de las rutas de desarrollo y producción.
const normalizeTrailingSlash = (path: string) => path.length > 1 ? path.replace(/\/+$/, "") : path;

const ChatButton: React.FC = () => {
    const { data } = useDataContext();
    const location = useLocation();

    // Se recalcula isHomePage, ya que no se envía más como prop
    const isHomePage = normalizeTrailingSlash(location.pathname) === normalizeTrailingSlash(data.pages.homePage.route)
                       || location.pathname === "/";

    const [isFixed, setIsFixed] = useState(!isHomePage);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isHomePage) {
            setIsFixed(false);
            return subscribeToScroll(setIsFixed);
        }
        setIsFixed(true);
    }, [isHomePage]);

    useEffect(() => {
        let resizeObserver: ResizeObserver | undefined;

        const stopWatchingContainer = () => {
            resizeObserver?.disconnect();
            resizeObserver = undefined;
        };

        const watchContainer = (container: HTMLElement) => {
            const updateIsOpen = () => {
                const { width, height } = container.getBoundingClientRect();
                setIsOpen(width > OPEN_SIZE_THRESHOLD_PX || height > OPEN_SIZE_THRESHOLD_PX);
            };

            updateIsOpen();
            resizeObserver = new ResizeObserver(updateIsOpen);
            resizeObserver.observe(container);
        };

        const existingContainer = document.querySelector<HTMLElement>(CHAT_WIDGET_CONTAINER_SELECTOR);
        if (existingContainer) {
            watchContainer(existingContainer);
        }

        // El script de Cloudbeds inyecta (y a veces remueve) el contenedor del
        // chat de forma asíncrona. Además de observar su tamaño hay que vigilar 
        // el <body> para engancharse apenas aparezca.
        const bodyObserver = new MutationObserver(() => {
            const container = document.querySelector<HTMLElement>(CHAT_WIDGET_CONTAINER_SELECTOR);
            if (container && !resizeObserver) {
                watchContainer(container);
            } else if (!container && resizeObserver) {
                stopWatchingContainer();
                setIsOpen(false);
            }
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            bodyObserver.disconnect();
            stopWatchingContainer();
        };
    }, []);

    return (
        <div className={`fixed bottom-6 lg:bottom-4 right-1 lg:right-3 z-[2000] size-14 lg:size-16
                       bg-forth-solid rounded-full border-4 border-first-solid
                         transition duration-500 ease-out opacity-0
                       hover:bg-third-solid
                      ${ isFixed ? "isFixed" : ""}`}>
            <button id={ CHAT_LAUNCHER_BUTTON_ID }
                    type="button"
                    aria-label="chat-launcher-button"
                    className="size-full flex justify-center items-center">
                {
                    isOpen
                        ? <CloseIcon className="size-6 lg:size-8 text-first-solid"/>
                        : <ChatIcon className="size-5 lg:size-6 text-first-solid"/>
                }
            </button>
        </div>
    )
}

export default ChatButton
